import { useState, useEffect, useRef, useCallback } from "react";
import { useToast } from "../contexts/ToastContext";

/**
 * Paginated fetch hook for dedicated feed tabs (Battles, Flex, Ask).
 * Handles loading, errors, toasts, and infinite scroll.
 */
export function useFeedDedicatedTab({
  active,
  fetchPage,
  errorLabel = "content",
  scrollRoot = null,
}) {
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const loadMoreRef = useRef(null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (pageNum = 1, append = false) => {
      const requestId = ++requestIdRef.current;
      if (pageNum === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      try {
        const { items: newItems, hasMore: more } = await fetchPage(pageNum);
        if (requestId !== requestIdRef.current) return;

        setItems((prev) => (append ? [...prev, ...newItems] : newItems));
        setHasMore(Boolean(more));
        setPage(pageNum);
        setError(null);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        const message =
          err.response?.data?.message || `Failed to load ${errorLabel}`;
        setError(message);
        showToast({ message, type: "error" });
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [fetchPage, errorLabel, showToast],
  );

  useEffect(() => {
    if (active) {
      load(1, false);
    }
  }, [active, load]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loading || !active) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          load(page + 1, true);
        }
      },
      { root: scrollRoot || null, rootMargin: "200px" },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, active, load, scrollRoot]);

  const retry = useCallback(() => load(1, false), [load]);

  return {
    items,
    setItems,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMoreRef,
    retry,
  };
}
